// /home/ec2-user/brand-insights-dashboard/frontend/src/components/DiscoverySidebar.js
import React, { useState } from 'react';
import {
  Box, Typography, Chip,
  Paper, Alert,
  Stack, Slider, IconButton,
  Collapse, FormControlLabel, Checkbox
} from '@mui/material';
import {
  FilterList, ExpandMore, ExpandLess
} from '@mui/icons-material';

// ============================================
// DISCOVERY SIDEBAR - SOLO FILTRI (SENZA BOTTONI)
// ============================================

const DiscoverySidebar = ({
  discoveryFilters = {},
  filterOptions = {},
  onFilterChange,
  onReset,          // 🔥 MANTENUTO per retrocompatibilità
  onSearch,         // 🔥 MANTENUTO per retrocompatibilità
  loading = false
}) => {
  // Stato UI puro per espansione delle sezioni (non correlato ai filtri business)
  const [expandedFilters, setExpandedFilters] = useState({
    visual_style: true,    // 🔥 PRIMO
    setting: false,        // 🔥 SECONDO
    primary_value: false,  // 🔥 TERZO
    content_type: false,   // 🔥 QUARTO
    target_implied: false, // 🔥 QUINTO
    cta_type: false,       // 🔥 SESTO
    sentiment: false,      // 🔥 SETTIMO
    intent: false,         // 🔥 OTTAVO
    category: false        // 🔥 NONO
  });

  // 🔥 SEZIONI DEI FILTRI DISCOVERY ORDINATE COME RICHIESTO
  const filterSections = [
    { key: 'visual_style', label: '🎨 Stile Visuale', icon: '🎨' },
    { key: 'setting', label: '🏞️ Setting/Ambientazione', icon: '🏞️' },
    { key: 'primary_value', label: '💰 Valore Primario', icon: '💰' },
    { key: 'content_type', label: '📄 Tipo Contenuto', icon: '📄' },
    { key: 'target_implied', label: '🎯 Target Implicito', icon: '🎯' },
    { key: 'cta_type', label: '📢 Tipo CTA', icon: '📢' },
    { key: 'sentiment', label: '😊 Sentiment', icon: '😊' },
    { key: 'intent', label: '🎯 Intent', icon: '🎯' },
    { key: 'category', label: '🏷️ Categoria', icon: '🏷️' }
  ];

  // Handler per toggle di un valore filtro
  const handleToggleFilter = (filterKey, value) => {
    if (!onFilterChange) {
      console.warn('onFilterChange non fornito al DiscoverySidebar');
      return;
    }

    const currentValues = discoveryFilters[filterKey] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    onFilterChange(filterKey, newValues);
  };

  // Handler per toggle espansione sezione (UI pura)
  const handleToggleSection = (key) => {
    setExpandedFilters(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Handler per cambio slider engagement minimo
  const handleMinEngagementChange = (value) => {
    if (onFilterChange) {
      onFilterChange('min_engagement', value);
    }
  };

  // 🔥 CALCOLO activeFilterCount
  const activeFilterCount = Object.entries(discoveryFilters).reduce((acc, [key, val]) => {
    // Conta solo i 9 filtri discovery principali + min_engagement
    const isDiscoveryFilter = filterSections.some(section => section.key === key);
    
    if (isDiscoveryFilter && Array.isArray(val)) {
      return acc + val.length;
    }
    
    if (key === 'min_engagement' && val > 0) {
      return acc + 1;
    }
    
    if (key === 'brand' && Array.isArray(val)) {
      return acc + val.length;
    }
    
    if (key === 'platform' && Array.isArray(val)) {
      return acc + val.length;
    }
    
    if (key === 'post_type' && Array.isArray(val)) {
      return acc + val.length;
    }
    
    return acc;
  }, 0);

  return (
    <Paper 
      elevation={0}
      sx={{
        width: { xs: '100%', md: 280 },
        flexShrink: 0,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        height: 'auto',
        minHeight: '400px',
        maxHeight: 'none',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        position: 'relative'
      }}
    >
      {/* Header - sempre visibile */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2, 
        pb: 2, 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        flexShrink: 0
      }}>
        <FilterList sx={{ mr: 1, color: 'primary.main' }} />
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: '1rem', 
            fontWeight: 'bold', 
            color: 'primary.main' 
          }}
        >
          Filtri Discovery
        </Typography>
        
        <Chip
          label={`${activeFilterCount} attivi`}
          size="small"
          color={activeFilterCount > 0 ? "primary" : "default"}
          variant={activeFilterCount > 0 ? "filled" : "outlined"}
          sx={{ ml: 'auto', fontSize: '0.75rem' }}
        />
      </Box>

      {/* Contenuto principale - solo filtri */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        minHeight: 0
      }}>
        {/* Slider Engagement Minimo */}
        <Box sx={{ 
          mb: 3, 
          p: 1.5, 
          bgcolor: 'grey.50', 
          borderRadius: 1,
          flexShrink: 0
        }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 1.5, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5 
            }}
          >
            🔥 Engagement Minimo
            {discoveryFilters.min_engagement > 0 && (
              <Chip 
                label={`≥${discoveryFilters.min_engagement}`} 
                size="small" 
                onDelete={() => handleMinEngagementChange(0)}
                sx={{ ml: 1, height: 20 }}
              />
            )}
          </Typography>
          
          <Box sx={{ px: 1 }}>
            <Slider
              value={discoveryFilters.min_engagement || 0}
              onChange={(e, value) => handleMinEngagementChange(value)}
              onChangeCommitted={(e, value) => handleMinEngagementChange(value)}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}+`}
              min={0}
              max={10000}
              step={100}
              marks={[
                { value: 0, label: '0' },
                { value: 2500, label: '2.5K' },
                { value: 5000, label: '5K' },
                { value: 7500, label: '7.5K' },
                { value: 10000, label: '10K' }
              ]}
              disabled={loading}
            />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            mt: 2,
            flexWrap: 'wrap' 
          }}>
            {[0, 1000, 5000, 10000].map((value) => (
              <Chip
                key={value}
                label={value === 0 ? 'Qualsiasi' : `${value}+`}
                size="small"
                variant={(discoveryFilters.min_engagement || 0) === value ? "filled" : "outlined"}
                color="primary"
                onClick={() => handleMinEngagementChange(value)}
                disabled={loading}
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        </Box>

        {/* 🔥 FILTRI A FACCETTE - ORDINATI COME RICHIESTO */}
        <Stack 
          spacing={2} 
          sx={{ 
            flex: 1,
            minHeight: 0,
            overflow: 'visible'
          }}
        >
          {filterSections.map((filter) => {
            // 🔥 CORREZIONE: key per category è 'categories' non 'categorys'
            const optionsKey = filter.key === 'category' ? 'categories' : `${filter.key}s`;
            const options = filterOptions[optionsKey] || [];
            const selectedValues = discoveryFilters[filter.key] || [];
            const isExpanded = expandedFilters[filter.key];
            
            return (
              <Paper 
                key={filter.key}
                elevation={0}
                sx={{ 
                  border: '1px solid', 
                  borderColor: selectedValues.length > 0 ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: selectedValues.length > 0 ? 'primary.50' : 'transparent',
                  opacity: loading ? 0.7 : 1,
                  transition: 'all 0.3s ease',
                  flexShrink: 0
                }}
              >
                {/* Header sezione */}
                <Box 
                  onClick={() => handleToggleSection(filter.key)}
                  sx={{ 
                    p: 1.5, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderBottom: isExpanded ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: selectedValues.length > 0 ? 'primary.50' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    }
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontSize: '0.9rem', 
                      fontWeight: 'medium',
                      color: selectedValues.length > 0 ? 'primary.dark' : 'text.primary'
                    }}
                  >
                    {filter.icon} {filter.label}
                    {selectedValues.length > 0 && (
                      <Chip 
                        label={selectedValues.length} 
                        size="small" 
                        color="primary"
                        sx={{ 
                          ml: 1, 
                          height: 20, 
                          fontSize: '0.7rem',
                          bgcolor: 'primary.main',
                          color: 'white'
                        }}
                      />
                    )}
                  </Typography>
                  <IconButton size="small" disabled={loading}>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                {/* Opzioni della sezione */}
                <Collapse 
                  in={isExpanded} 
                  timeout="auto"
                  unmountOnExit={false}
                  sx={{
                    '& .MuiCollapse-wrapper': {
                      overflow: 'visible'
                    }
                  }}
                >
                  <Box sx={{ 
                    p: 1,
                    height: 'auto',
                    minHeight: 0
                  }}>
                    {options.length === 0 ? (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          p: 1, 
                          display: 'block', 
                          fontStyle: 'italic',
                          fontSize: '0.8rem'
                        }}
                      >
                        {loading ? 'Caricamento...' : 'Nessuna opzione disponibile'}
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {options.slice(0, 20).map((option) => (
                          <FormControlLabel
                            key={option.value}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedValues.includes(option.value)}
                                onChange={() => handleToggleFilter(filter.key, option.value)}
                                disabled={loading}
                              />
                            }
                            label={
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: '0.85rem',
                                  color: selectedValues.includes(option.value) 
                                    ? 'primary.dark' 
                                    : 'text.primary'
                                }}
                              >
                                {option.label}
                              </Typography>
                            }
                            sx={{ 
                              m: 0,
                              '&:hover': {
                                bgcolor: 'action.hover',
                                borderRadius: 0.5
                              }
                            }}
                          />
                        ))}
                        
                        {options.length > 20 && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              pl: 2, 
                              pt: 0.5,
                              fontStyle: 'italic',
                              fontSize: '0.75rem'
                            }}
                          >
                            ... e altre {options.length - 20} opzioni
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>

        {/* 🔥 MOSTRA SOLO FILTRI ATTIVI (senza Alert) */}
        {activeFilterCount > 0 && (
          <Box sx={{ 
            mt: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.default',
            flexShrink: 0
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '0.75rem',
                display: 'block',
                mb: 1,
                color: 'text.primary'
              }}
            >
              📊 Filtri attivi:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.5
            }}>
              {Object.entries(discoveryFilters).map(([key, value]) => {
                if (!value || value.length === 0) return null;
                
                if (Array.isArray(value)) {
                  return value.map((val, idx) => (
                    <Chip
                      key={`${key}-${idx}`}
                      label={`${key}: ${val}`}
                      size="small"
                      onDelete={() => handleToggleFilter(key, val)}
                      sx={{ 
                        fontSize: '0.65rem',
                        height: 18,
                        maxWidth: 120
                      }}
                    />
                  ));
                }
                
                if (key === 'min_engagement' && value > 0) {
                  return (
                    <Chip
                      key={key}
                      label={`engagement ≥ ${value}`}
                      size="small"
                      onDelete={() => handleMinEngagementChange(0)}
                      sx={{ 
                        fontSize: '0.65rem',
                        height: 18,
                        maxWidth: 120
                      }}
                    />
                  );
                }
                
                return null;
              }).flat().filter(Boolean)}
            </Box>
          </Box>
        )}
        
      </Box>
    </Paper>
  );
};

// Props validation
DiscoverySidebar.defaultProps = {
  discoveryFilters: {},
  filterOptions: {},
  loading: false
};

export default DiscoverySidebar;